import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Lock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForcePasswordChange = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('One number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      errors.push('One special character');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    
    if (!formData.current_password) {
      newErrors.current_password = 'Current password is required';
    }
    
    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else {
      const passwordErrors = validatePassword(formData.new_password);
      if (passwordErrors.length > 0) {
        newErrors.new_password = `Missing: ${passwordErrors.join(', ')}`;
      }
    }
    
    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      toast.success('Password changed successfully!');
      
      // Redirect to appropriate page
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      if (error.response?.data?.detail?.includes('Current password') || error.response?.data?.detail?.includes('incorrect')) {
        setErrors({ current_password: 'Incorrect current password' });
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    'At least 8 characters long',
    'One uppercase letter (A-Z)',
    'One lowercase letter (a-z)',
    'One number (0-9)',
    'One special character (!@#$%^&*...)'
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-background p-4">
      <Card className="w-full max-w-md border-2 border-border">
        <CardHeader className="space-y-1 pb-4">
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full">
              <Lock className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-foreground">
            Password Change Required
          </CardTitle>
          <p className="text-center text-sm text-muted-foreground">
            For security reasons, you must change your password before continuing
          </p>
        </CardHeader>
        
        <CardContent>
          {/* Security Alert */}
          <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-orange-800 dark:text-orange-200">
                  Temporary Password Detected
                </p>
                <p className="text-orange-700 dark:text-orange-300 mt-1">
                  Your account is using a temporary password. Please create a new secure password to continue.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Current Password (Temporary)
              </label>
              <Input
                type="password"
                value={formData.current_password}
                onChange={(e) => {
                  setFormData({ ...formData, current_password: e.target.value });
                  setErrors({ ...errors, current_password: null });
                }}
                placeholder="Enter temporary password"
                autoComplete="current-password"
                className={errors.current_password ? 'border-red-500' : ''}
              />
              {errors.current_password && (
                <p className="text-xs text-red-600 mt-1">{errors.current_password}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                New Password
              </label>
              <Input
                type="password"
                value={formData.new_password}
                onChange={(e) => {
                  setFormData({ ...formData, new_password: e.target.value });
                  setErrors({ ...errors, new_password: null });
                }}
                placeholder="Enter new password"
                autoComplete="new-password"
                className={errors.new_password ? 'border-red-500' : ''}
              />
              {errors.new_password && (
                <p className="text-xs text-red-600 mt-1">{errors.new_password}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Confirm New Password
              </label>
              <Input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => {
                  setFormData({ ...formData, confirm_password: e.target.value });
                  setErrors({ ...errors, confirm_password: null });
                }}
                placeholder="Confirm new password"
                autoComplete="new-password"
                className={errors.confirm_password ? 'border-red-500' : ''}
              />
              {errors.confirm_password && (
                <p className="text-xs text-red-600 mt-1">{errors.confirm_password}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs font-semibold text-foreground mb-2">Password Requirements:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                {passwordRequirements.map((req, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-semibold disabled:opacity-50"
            >
              {loading ? 'Changing Password...' : 'Change Password & Continue'}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
